import { useTranslation } from 'next-i18next'
import { type FC, useState } from 'react'
import { useTheme } from 'styled-components'

import { ShowAtOrLarger, ShowWhenSmaller } from '@/components/shared/breakpoint'
import { ClientRender } from '@/components/shared/client-render'
import { ImageYAMS } from '@/components/shared/image/ImageYAMS'
import { FavouriteButton } from '@/components/shared/listing/FavouriteButton'
import { AutosAttributeList } from '@/components/srp/listing-card/autos-attribute-list'
import { AutosBadges } from '@/components/srp/listing-card/autos-badges'
import { AutosCrossPromoListingCard } from '@/components/srp/listing-card/autos-x-promo-listing-card/AutosCrossPromoListingCard'
import { GetMediaFavouriteButtonSize } from '@/components/srp/listing-card/get-media-favourite-button-size'
import { ListingDetails } from '@/components/srp/listing-card/listing-details'
import { ListingDetailsDividerStyle } from '@/components/srp/listing-card/listing-details/ListingDetails'
import { ListingImageBadge } from '@/components/srp/listing-card/listing-image-badge'
import { ListingPrice } from '@/components/srp/listing-card/listing-price'
import { ListingPriceVariant } from '@/components/srp/listing-card/listing-price/ListingPrice'
import {
  AutosCardDescriptionContainer,
  AutosListingCardBottomData,
  AutosListingCardContainer,
  AutosListingCardDivider,
  AutosListingCardHeading,
  AutosListingCardLogoContainer,
  AutosListingCardTopData,
  AutosListingImageBase,
  ExpandDescriptionButton,
  ListingCardBody,
  ListingCardDetails,
  ListingCardLink,
  ListingRightSide,
} from '@/components/srp/listing-card/styled'
import {
  type AutosListingAttributes,
  type AutosListingCardProps,
} from '@/components/srp/listing-card/types'
import {
  getCarsVehiclesAttributes,
  getTransmissionTypeLiteralKey,
  SRP_ATTRIBUTES,
} from '@/components/srp/search-list/domain'
import { highlightKeywords } from '@/components/srp/search-list/highlightKeywords'
import { Showcase } from '@/components/srp/showcase'
import { autosListingMainImageUrlRuleSize, autosShowcaseThumbnailSizes } from '@/constants/listings'
import { getListingBadgeLabel } from '@/domain/listings/getListingBadgeLabel'
import { getListingProximityLabel } from '@/domain/srp/getListingProximityLabel'
import { truncateText } from '@/domain/srp/truncateText'
import { ATTRIBUTES } from '@/features/attributes/constants/attributes'
import { getAttributesCanonicalValuesDictionary } from '@/features/attributes/utils/getAttributesCanonicalValuesDictionary'
import { useGetSearchKeyword } from '@/hooks/keywords/useGetSearchKeywords'
import { useLocale } from '@/hooks/useLocale'
import { BadgeSize } from '@/ui/atoms/badge/Badge'
import { BodyText } from '@/ui/atoms/body-text'
import { RotatingChevron } from '@/ui/atoms/rotating-chevron'
import { Spacing } from '@/ui/atoms/spacing'

/**
 * `AutosListingCard` is a component that renders a listing card for autos categories
 *
 * @component
 */
export const AutosListingCard: FC<AutosListingCardProps> = ({
  item,
  isMobile,
  index,
  price,
  isCrossPromotionCard,
  isInTopAdsSection,
  handleTrackListingClick,
}) => {
  const { colors, spacing } = useTheme()
  const { t } = useTranslation('srp')
  const { routeLocale } = useLocale()
  const { keyword: globalKeyword } = useGetSearchKeyword()

  const [showDescription, setShowDescription] = useState<boolean>(false)

  const { id, categoryId, attributes, flags, url, title, imageUrls, adSource, sortingDate } = item

  const attributesDictionary = getAttributesCanonicalValuesDictionary(attributes?.all)
  const { carfax, cpo, dealerUpdates, isNew } = getCarsVehiclesAttributes(
    attributesDictionary,
    categoryId,
    routeLocale,
    t
  )
  const msrp = (isNew && price.msrp) || undefined

  const listingAttributes: AutosListingAttributes = {
    dealerUpdates,
    hasCarfaxReport: carfax,
    isCertifiedPreOwned: cpo,
    msrp,
    km: attributesDictionary[ATTRIBUTES.CAR_MILEAGE]?.[0],
    transmission: attributesDictionary[ATTRIBUTES.CAR_TRANSMISSION]?.[0]
      ? t(getTransmissionTypeLiteralKey(+attributesDictionary[ATTRIBUTES.CAR_TRANSMISSION]?.[0]))
      : undefined,
  }

  const { hasCarfaxReport, isCertifiedPreOwned } = listingAttributes

  const shouldShowAttributes = Object.keys(listingAttributes)?.length > 0
  const hasContactlessServices = !!dealerUpdates.length
  const description = item.description || ''

  const logoSrc = attributesDictionary[SRP_ATTRIBUTES.LOGO]?.[0]

  const shouldRenderBottomData =
    logoSrc || description || hasCarfaxReport || isCertifiedPreOwned || hasContactlessServices

  const shouldRenderBadges = hasCarfaxReport || isCertifiedPreOwned || hasContactlessServices

  const { showcase, highlight, categorySpecificBadge } = flags

  const showcaseImages = showcase ? item.imageUrls?.slice(1) : []

  const renderShowcase = () => {
    if (!showcaseImages?.length) return null

    return (
      <Showcase
        thumbnailSizes={autosShowcaseThumbnailSizes}
        itemBorderRadius="small"
        itemListGap="defaultSmall"
        maxNumberOfPictures={9}
        seoUrl={url}
        showcaseImages={showcaseImages}
        handleListingCardClick={handleTrackListingClick}
        showDivider={false}
        baseAltText={truncateText(description, 100)}
      />
    )
  }

  const getListingImageSize = () => {
    return highlight ? 'xxLarge' : 'xLarge'
  }

  const expandDescription = () => {
    setShowDescription(!showDescription)
  }

  /** If is a conventional listing, then find all the values needed to render it */
  const imageBadge = getListingBadgeLabel({
    categoryId: categoryId,
    categorySpecificBadge: !!categorySpecificBadge,
  })

  const mainImage = imageUrls?.[0]

  /**
   * Distance
   * Should return the proximity value from the user in meters
   */
  const proximity = getListingProximityLabel(item.location)

  if (isCrossPromotionCard) {
    return (
      <AutosCrossPromoListingCard
        item={item}
        attributes={attributesDictionary}
        price={price}
        msrp={msrp}
        isMobile={isMobile}
      />
    )
  }
  return (
    <AutosListingCardContainer data-testid="listing-card" data-listingid={id}>
      <AutosListingCardTopData isHighlight={!!highlight}>
        <AutosListingImageBase
          badge={imageBadge ? <ListingImageBadge imageBadge={imageBadge} /> : null}
          data-testid={`listing-card-image-container${highlight ? '-highlight' : ''}`}
          imageComponent={
            <ImageYAMS
              alt={truncateText(description, 100) || title}
              data-testid="listing-card-image"
              src={mainImage}
              width={autosListingMainImageUrlRuleSize}
              height={300}
              extension="WEBP"
              priority={isInTopAdsSection && index === 0}
              loading={index < 2 ? 'eager' : 'lazy'}
            />
          }
          size={getListingImageSize()}
        />
        <ListingCardDetails>
          <ListingCardBody>
            <ListingPrice
              displayInlinePrice={!!highlight}
              msrp={msrp}
              priceAmount={price.amount}
              priceOriginal={price.original}
              type={price.type}
              surcharges={price.surcharges}
              variant={ListingPriceVariant.Autos}
            />
            <AutosListingCardHeading
              data-testid="listing-title"
              forwardedAs="h3"
              color={colors.grey.primary}
              size="small"
              weight={isMobile ? 'medium' : 'regular'}
            >
              <ListingCardLink
                data-testid="listing-link"
                href={url}
                onClick={handleTrackListingClick}
                variant="grey"
              >
                {highlightKeywords(id, title, globalKeyword)}
              </ListingCardLink>
            </AutosListingCardHeading>

            {shouldShowAttributes ? <AutosAttributeList attributes={listingAttributes} /> : null}

            <Spacing mBottom={spacing.defaultSmall}>
              <ListingDetails
                sortingDate={isInTopAdsSection ? undefined : sortingDate}
                locationName={item.location.name}
                proximity={proximity}
                listingAdSource={adSource}
                dividerStyle={ListingDetailsDividerStyle.DOT}
                isMobileFormat={isMobile}
              />
            </Spacing>
            <ShowAtOrLarger breakpoint="medium">{renderShowcase()}</ShowAtOrLarger>
          </ListingCardBody>

          <ListingRightSide>
            <ClientRender>
              <GetMediaFavouriteButtonSize>
                {({ size }) => <FavouriteButton colorScheme="purple" listingId={id} size={size} />}
              </GetMediaFavouriteButtonSize>
            </ClientRender>
          </ListingRightSide>
        </ListingCardDetails>
      </AutosListingCardTopData>
      <ShowWhenSmaller breakpoint="medium">{renderShowcase()}</ShowWhenSmaller>
      {shouldRenderBottomData && (
        <>
          <AutosListingCardDivider
            top={spacing.defaultSmall}
            bottom={spacing.defaultSmall}
            showDivider={!!(logoSrc || shouldRenderBadges)}
          />

          <AutosListingCardBottomData
            flexWrap="wrap"
            gap={logoSrc ? spacing.default : '0'}
            alignItems="center"
          >
            {logoSrc && (
              <AutosListingCardLogoContainer>
                <ImageYAMS data-testid="logo-container" fill src={logoSrc} alt="" />
              </AutosListingCardLogoContainer>
            )}
            {shouldRenderBadges && (
              <AutosBadges horizontalOverflow hasDescription={!!description}>
                {isCertifiedPreOwned && (
                  <AutosBadges.CertifiedPreOwned size={BadgeSize.LARGE} variant="badge" />
                )}
                {hasCarfaxReport && <AutosBadges.CarFax size={BadgeSize.LARGE} variant="badge" />}
                {hasContactlessServices && (
                  <AutosBadges.Contactless size={BadgeSize.LARGE} variant="badge" />
                )}
              </AutosBadges>
            )}
            {description && (
              <ExpandDescriptionButton
                onClick={expandDescription}
                data-testid="listing-description-button"
                aria-label={t('listing.description_button.label')}
                aria-expanded={showDescription}
                aria-controls={`expandable-description-${id}`}
              >
                <RotatingChevron isOpen={showDescription} />
              </ExpandDescriptionButton>
            )}
          </AutosListingCardBottomData>
          <AutosCardDescriptionContainer
            showDescription={showDescription}
            id={`expandable-description-${id}`}
          >
            <BodyText color={colors.grey.primary} data-testid="listing-description" size="small">
              {highlightKeywords(id, description, globalKeyword)}
            </BodyText>
          </AutosCardDescriptionContainer>
        </>
      )}
    </AutosListingCardContainer>
  )
}
